/* The Telegram Mini App sign-in, as a decision rather than an effect.

   Opened inside Telegram, the page receives `Telegram.WebApp.initData` — a
   signed query string naming the Telegram user. The site does not validate
   it; it hands it to `POST /signin/telegram/mini-app` as received and the
   platform checks the signature against the site's bot token.

   Three rules, the whitelabel's (`TelegramWebAppProvider`):

   - Only inside Telegram: an empty `initData` is a plain browser, and nothing
     happens.
   - `/whoami` first. A player already signed in (a session from an earlier
     open) is not signed in again; `requireUnauthenticated` would refuse it
     anyway, and the refusal would show as a failure.
   - Once per page load. The sign-in rotates the session; firing it again on a
     re-render — or twice under React's strict mode — would race two
     rotations against each other. */

export type WhoamiState = "pending" | "signed-in" | "signed-out" | "error";

export type MiniAppStep =
  /* Not in Telegram, or already decided: nothing to do. */
  | "idle"
  /* In Telegram, waiting on `/whoami`. */
  | "wait"
  /* In Telegram and signed out: post the initData, once. */
  | "sign-in"
  /* In Telegram and already signed in. */
  | "done";

export const miniAppStep = ({
  initData,
  whoami,
  started,
}: {
  initData: string | null | undefined;
  whoami: WhoamiState;
  started: boolean;
}): MiniAppStep => {
  if (started || !initData) {
    return "idle";
  }

  if (whoami === "signed-out") {
    return "sign-in";
  }

  if (whoami === "signed-in") {
    return "done";
  }

  /* Pending, or a transient failure the query is retrying: a sign-in sent now
     could be for a player who is in fact signed in. */
  return "wait";
};

/* A latch: `run` calls `work` the first time and never again. */
export const once = () => {
  let ran = false;

  return {
    get ran() {
      return ran;
    },
    run(work: () => void) {
      if (ran) {
        return false;
      }

      ran = true;
      work();
      return true;
    },
  };
};

export type Latch = ReturnType<typeof once>;

/* One look at the state, from the component's effect: posts the initData the
   first time the player is found signed out, closes the latch the first time
   they are found signed in, and otherwise waits. */
export const advanceMiniApp = (
  latch: Latch,
  { initData, whoami }: { initData: string | null | undefined; whoami: WhoamiState },
  signIn: (initData: string) => void,
): MiniAppStep => {
  const step = miniAppStep({ initData, whoami, started: latch.ran });

  if ((step === "sign-in" || step === "done") && initData) {
    latch.run(() => {
      if (step === "sign-in") signIn(initData);
    });
  }

  return step;
};

/* Telegram's own client on iOS swipes the Mini App closed on a downward
   scroll unless vertical swipes are switched off. */
export const isTelegramIos = (platform: string | undefined) => platform === "ios";

export const TELEGRAM_WEB_APP_SCRIPT = "https://telegram.org/js/telegram-web-app.js";

export const TELEGRAM_WEB_APP_SCRIPT_ID = "telegram-web-app";
